import { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtEur } from "../../lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { SortableHead } from "../SortableHead";
import { useSortableTable } from "../../hooks/useSortableTable";
import { AlertTriangle } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");

// Cella "Fondo" editabile: salva al blur / Invio se il valore è cambiato.
const FondoCell = ({ row, year, onSaved }) => {
    const [val, setVal] = useState(row.fondo ?? "");
    const [saving, setSaving] = useState(false);
    const save = async () => {
        const n = val === "" ? null : parseFloat(String(val).replace(",", "."));
        if (val !== "" && (Number.isNaN(n) || n < 0)) { toast.error("Importo non valido"); return; }
        if ((row.fondo ?? null) === n) return;
        setSaving(true);
        try {
            await api.put(`/tfr/funds/${year}/${row.employee_id}`, { fondo: n });
            onSaved(row.employee_id, n);
        } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); setVal(row.fondo ?? ""); }
        finally { setSaving(false); }
    };
    return (
        <input
            type="number" step="0.01" min="0" value={val} disabled={saving}
            onChange={(e) => setVal(e.target.value)} onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            className="h-8 w-32 rounded border border-[#376DB3]/40 bg-white px-2 text-right font-mono-numbers text-sm focus:border-[#376DB3] focus:outline-none"
            data-testid={`tfr-fondo-input-${row.employee_id}`}
        />
    );
};

// Badge precisione del lordo scalato: da cedolino (preciso) / stimato con aliquota / misto.
export const PrecisionBadge = ({ row, aliquota }) => {
    if (!row.anticipi_count && !row.n_cedolini_senza_ricevuta) return null;
    const map = {
        preciso: ["bg-emerald-50 text-emerald-800 ring-emerald-200", `da cedolin${row.n_cedolini === 1 ? "o" : "i"}`],
        stimato: ["bg-amber-50 text-amber-900 ring-amber-200", `stimato ${aliquota}%`],
        parziale: ["bg-amber-50 text-amber-900 ring-amber-200", `${row.n_cedolini} cedolin${row.n_cedolini === 1 ? "o" : "i"} · ${row.n_stimati} stimat${row.n_stimati === 1 ? "o" : "i"}`],
    };
    const [cls, label] = map[row.precision] || map.stimato;
    return (
        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${cls}`} data-testid={`tfr-precision-${row.employee_id}`} title={row.n_cedolini_senza_ricevuta ? `${row.n_cedolini_senza_ricevuta} cedolino/i senza ricevuta nel portale` : undefined}>
            {label}{row.n_cedolini_senza_ricevuta ? " · senza ricevuta" : ""}
        </span>
    );
};

export const TfrSaldiTable = ({ rows, year, prevYearEnd, editMode, aliquota, onSaved }) => {
    const { sortedItems, sortKey, sortDir, requestSort } = useSortableTable(rows, {
        customAccessors: { matricola: (r) => parseInt(r.matricola, 10) || 0, dipendente: (r) => `${r.cognome} ${r.nome}` },
    });
    const H = (key, label, align) => (
        <SortableHead sortKey={key} sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} align={align} className={`text-xs ${align === "right" ? "text-right" : ""}`}>{label}</SortableHead>
    );
    return (
        <Table data-testid="tfr-saldi-table">
            <TableHeader>
                <TableRow className="bg-slate-50">
                    {H("matricola", "Matr.")}
                    {H("dipendente", "Dipendente")}
                    {H("fondo", `Fondo al ${prevYearEnd}`, "right")}
                    {H("anticipi", `Anticipi ${year} (netto)`, "right")}
                    {H("anticipi_lordo", "Lordo scalato", "right")}
                    {H("residuo", "Residuo lordo", "right")}
                    {H("netto", "Netto stimato", "right")}
                    <TableHead className="text-xs">Aggiornato</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {sortedItems.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-slate-500">Nessun dipendente corrisponde ai filtri.</TableCell></TableRow>
                )}
                {sortedItems.map((r) => (
                    <TableRow key={r.employee_id} className={r.fondo === null ? "bg-amber-50/40" : ""} data-testid={`tfr-row-${r.employee_id}`}>
                        <TableCell className="font-mono-numbers text-xs text-slate-500">{r.matricola || "—"}</TableCell>
                        <TableCell className="text-sm font-medium text-slate-800">
                            {r.cognome} {r.nome}
                            {r.cessato && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">Cessato</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono-numbers text-sm">
                            {editMode ? <FondoCell row={r} year={year} onSaved={onSaved} /> : r.fondo === null ? <span className="text-xs italic text-amber-700">da inserire</span> : fmtEur(r.fondo)}
                            {r.disponibile_delta !== null && r.disponibile_delta !== undefined && (
                                <span className="ml-1 inline-flex align-middle text-red-600" title={`Il cedolino riporta ${fmtEur(r.disponibile_cedolino)} disponibili al ${r.disponibile_at} (${r.disponibile_delta > 0 ? "+" : ""}${fmtEur(r.disponibile_delta)} rispetto al saldo)`} data-testid={`tfr-disp-mismatch-${r.employee_id}`}>
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                </span>
                            )}
                        </TableCell>
                        <TableCell className="text-right font-mono-numbers text-sm">
                            {r.anticipi ? <>{fmtEur(r.anticipi)} <span className="text-[10px] text-slate-400">({r.anticipi_count})</span></> : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono-numbers text-xs text-slate-600">
                            {r.anticipi_lordo ? <div>{fmtEur(r.anticipi_lordo)}</div> : "—"}
                            <PrecisionBadge row={r} aliquota={aliquota} />
                        </TableCell>
                        <TableCell className={`text-right font-mono-numbers text-sm font-semibold ${r.residuo !== null && r.residuo < 0 ? "text-red-600" : "text-slate-800"}`}>
                            {r.residuo === null ? "—" : fmtEur(r.residuo)}
                        </TableCell>
                        <TableCell className="text-right font-mono-numbers text-sm text-emerald-700">{r.netto === null ? "—" : fmtEur(r.netto)}</TableCell>
                        <TableCell className="text-[11px] text-slate-500">
                            {r.fondo_updated_at && <>{fmtDate(r.fondo_updated_at)}<br /><span className="text-slate-400">{r.fondo_source === "file" ? "file consulente" : r.fondo_updated_by}</span></>}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};
